/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\utils\route-guards.ts
 * 路由守卫工具函数
 */

// 说明：本项目的TS配置可能导致直接从 'vue-router' 导入类型时被解析为命名空间。
// 采用别名导入并通过 typeof 实例参数推断，保持严格类型且规避 linter 误报。
// 使用官方 vue-router v4 类型
// 兼容某些 TS 配置下命名空间类型解析问题：通过类型别名再导出，避免 linter 识别为命名空间。
// 最终退回到结构类型别名，保持严格（非 unknown），避免命名空间冲突
type Router = {
  beforeEach: (
    guard: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => void | Promise<void>
  ) => void
}
type RouteLocationNormalized = { path: string; fullPath: string; meta?: Record<string, unknown> }
type NavigationGuardNext = (to?: string | { path: string; query?: Record<string, unknown> } | void) => void
import { TokenManager } from '../api'
import type { User } from '../types'

export interface AuthGuardOptions {
  loginPath?: string
  homePath?: string
  excludeRoutes?: string[]
  requirePermissions?: string[]
  requireRoles?: string[]
  guidancePath?: string
}

/**
 * 设置全局认证守卫
 * @param router - Vue Router 实例
 * @param options - 守卫配置选项
 */
export function setupAuthGuards(
  router: Router, 
  options: AuthGuardOptions = {}
) {
  const {
    loginPath = '/login',
    homePath = '/',
    excludeRoutes = ['/login', '/forgot-password', '/reset-password', '/access-guidance'],
    requireRoles = [],
    guidancePath = '/access-guidance'
  } = options

  // 全局前置守卫
  router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    // 关键节点日志（开发环境可开启）：进入守卫
    if (import.meta?.env?.DEV) {
      try {
        console.info('[AuthGuard] start', { to: to.path })
      } catch {}
    }
    // 检查是否为排除路由
    if (excludeRoutes.includes(to.path)) {
      if (import.meta?.env?.DEV) console.info('[AuthGuard] passthrough:', to.path)
      // 如果是登录页面且本地已有token：
      // 1) 若token对应用户的角色被本应用允许，则重定向到首页
      // 2) 若不被允许（例如拿着商户token访问运营商登录页），清除token并留在登录页
      if (to.path === loginPath && TokenManager.hasValidAuth()) {
        try {
          if (import.meta?.env?.DEV) console.info('[AuthGuard] validating token on loginPath')
          const user = await getCurrentUser()
          if (import.meta?.env?.DEV) console.info('[AuthGuard] current user fetched:', { hasUser: !!user })
          if (user && (requireRoles.length === 0 || checkRoles(user, requireRoles))) {
            if (import.meta?.env?.DEV) console.info('[AuthGuard] redirect home')
            next(homePath)
            return
          }
          // 角色不匹配，清理并停留在登录页
          if (import.meta?.env?.DEV) console.warn('[AuthGuard] role mismatch on loginPath')
          try {
            const event = new CustomEvent('auth-failure', {
              detail: {
                reason: 'role_mismatch',
                expected: requireRoles.join(','),
                actual: user ? getUserRole(user) : 'unknown'
              }
            })
            window.dispatchEvent(event)
          } catch {}
          TokenManager.clearTokens()
          next()
          return
        } catch (e) {
          // token 失效等异常，清理并停留在登录页
          if (import.meta?.env?.DEV) console.warn('[AuthGuard] token invalid on loginPath')
          TokenManager.clearTokens()
          next()
          return
        }
      }
      if (import.meta?.env?.DEV) console.info('[AuthGuard] excluded route passthrough:', to.path)
      next()
      return
    }

    // 检查是否需要认证（默认需要，除非显式 requiresAuth: false）
    if (requiresAuth(to)) {
      if (import.meta?.env?.DEV) console.info('[AuthGuard] need auth:', to.path)

      // ⭐ 使用 hasValidAuth() 检查 token 有效性（含过期判断），
      // 而非仅检查 token 是否存在。双 token 均过期时自动清除并跳登录。
      if (!TokenManager.hasValidAuth()) {
        if (import.meta?.env?.DEV) console.warn('[AuthGuard] no valid auth, go login')
        clearUserCache()
        next({
          path: loginPath,
          query: { redirect: to.fullPath }
        })
        return
      }

      // access_token 过期但 refresh_token 有效时，强制从服务端获取用户信息（触发 refresh）
      const forceRefresh = TokenManager.isAccessTokenExpired()

      // 已认证但需要检查权限
      try {
        if (import.meta?.env?.DEV) console.info('[AuthGuard] fetch current user, force:', forceRefresh)
        const user = await getCurrentUser(forceRefresh)
        if (!user) {
          // 用户信息获取失败，清除token并重定向到登录页
          if (import.meta?.env?.DEV) console.warn('[AuthGuard] fetch user failed')
          TokenManager.clearTokens()
          next({
            path: loginPath,
            query: { redirect: to.fullPath }
          })
          return
        }

        // 检查路由级别的权限要求
        const routePermissions = getRoutePermissions(to)
        const routeRoles = getRouteRoles(to)
        if (import.meta?.env?.DEV) console.info('[AuthGuard] meta', { routeRoles })

        // 先检查应用级允许的角色（可选）。若不匹配，清理token并跳登录页
        const allowAppRole = requireRoles.length === 0 || checkRoles(user, requireRoles)
        if (!allowAppRole) {
          const actualRole = getUserRole(user)
          // 在清理 token 前派发事件，通知登录页给出友好提示
          const event = new CustomEvent('auth-failure', {
            detail: { reason: 'role_mismatch', expected: requireRoles.join(','), actual: actualRole }
          })
          window.dispatchEvent(event)
          if (import.meta?.env?.DEV) console.warn('[AuthGuard] app role mismatch')
          TokenManager.clearTokens()
          next({ path: loginPath, query: { redirect: to.fullPath } })
          return
        }
        // 再检查路由级权限与角色
        const allowRouteRole = checkRoles(user, routeRoles)
        if (!checkPermissions(user, routePermissions) || !allowRouteRole) {
          const reason = !allowRouteRole ? 'role_denied' as const : 'permission_denied' as const
          if (import.meta?.env?.DEV) console.warn('[AuthGuard] route denied')
          // 分发无权事件，供登录页或其他组件监听
          try {
            const event = new CustomEvent('auth-failure', {
              detail: {
                reason,
                targetPath: to.fullPath,
                requiredRoles: routeRoles,
                requiredPermissions: routePermissions
              }
            })
            window.dispatchEvent(event)
          } catch {}

          // 跳转到访问引导页，携带必要上下文
          next({
            path: guidancePath,
            query: {
              targetPath: to.fullPath,
              requiredRoles: routeRoles.join(','),
              requiredPermissions: routePermissions.join(','),
              reason
            }
          })
          return
        }

        if (import.meta?.env?.DEV) console.info('[AuthGuard] ok')
        next()
      } catch (error) {
        console.error('Auth guard error:', error)
        // 认证检查失败，清除token并重定向到登录页
        TokenManager.clearTokens()
        next({
          path: loginPath,
          query: { redirect: to.fullPath }
        })
      }
    } else {
      // 显式无需认证的页面（登录、403、404 等），直接放行
      console.info('[AuthGuard] route does not require auth, passthrough:', to.path)
      next()
    }
  })
}

/**
 * 检查路由是否需要认证
 * @param route - 路由对象
 * @returns 是否需要认证
 */
function requiresAuth(route: RouteLocationNormalized): boolean {
  return route.meta?.requiresAuth !== false
}

/**
 * 获取路由的权限要求
 * @param route - 路由对象
 * @returns 权限列表
 */
function getRoutePermissions(route: RouteLocationNormalized): string[] {
  return route.meta?.permissions as string[] || []
}

/**
 * 获取路由的角色要求
 * @param route - 路由对象
 * @returns 角色列表
 */
function getRouteRoles(route: RouteLocationNormalized): string[] {
  return route.meta?.roles as string[] || []
}

/**
 * 检查用户权限
 * @param user - 用户对象
 * @param requiredPermissions - 需要的权限列表
 * @returns 是否有权限
 */
function checkPermissions(user: User, requiredPermissions: string[]): boolean {
  if (requiredPermissions.length === 0) return true
  if (!user.permissions) return false
  
  // 超级用户拥有所有权限
  if (user.is_superuser) return true
  
  return requiredPermissions.some(permission => 
    user.permissions.includes(permission)
  )
}

/**
 * 检查用户角色
 * @param user - 用户对象
 * @param requiredRoles - 需要的角色列表
 * @returns 是否有角色权限
 */
function checkRoles(user: User, requiredRoles: string[]): boolean {
  if (requiredRoles.length === 0) return true
  
  // 根据用户归属与后端可能返回的 user_type 字段综合判断角色
  let userRole = ''
  if (user.operator_department_id) {
    userRole = 'operator'
  } else if (user.merchant_id) {
    userRole = 'merchant'
  } else if (user.enterprise_id) {
    userRole = 'enterprise'
  } else if (user.user_type && ['operator', 'merchant', 'enterprise'].includes(user.user_type)) {
    // 兼容后端直接返回 user_type 的场景
    userRole = user.user_type
  }
  
  // ⭐ 登录访问权限口径修复：
  // - 超级用户（superuser）默认仅允许运营端访问（视为 operator 放行）
  // - 商户端/企业端若不显式允许运营端角色，则应拦截 superuser，避免“能登录但进 404”的体验
  if (user.is_superuser) {
    if (requiredRoles.includes('operator')) return true
    return requiredRoles.includes('superuser')
  }
  
  return requiredRoles.includes(userRole)
}

/**
 * 计算用户角色字符串
 */
function getUserRole(user: User): string {
  if (user.is_superuser) return 'superuser'
  if (user.operator_department_id) return 'operator'
  if (user.merchant_id) return 'merchant'
  if (user.enterprise_id) return 'enterprise'
  if (user.user_type && ['operator', 'merchant', 'enterprise'].includes(user.user_type)) {
    return user.user_type
  }
  return 'unknown'
}

/**
 * 用户信息缓存（避免每次导航都请求 API）
 * ⭐ 关键修复（2025-12-28）：Tab 切换触发的导航不应该重新请求用户信息
 * ⭐ P0 修复：与 Pinia userStore 共享缓存，避免重复请求
 */
let cachedUser: User | null = null
let cacheTimestamp = 0
const CACHE_TTL = 5 * 60 * 1000  // 5分钟缓存有效期

/**
 * 获取当前用户信息（带多级缓存）
 * @param force - 是否强制刷新（默认 false）
 * @returns 用户信息Promise
 * 
 * 缓存策略（优先级从高到低）：
 * 1. 本地内存缓存（最快）
 * 2. Pinia userStore.currentUser（持久化）
 * 3. 调用 userStore.fetchCurrentUser() 请求 API
 * 
 * 登出时需调用 clearUserCache() 清除缓存
 */
async function getCurrentUser(force = false): Promise<User | null> {
  const now = Date.now()
  
  // ⭐ 第一级：检查本地内存缓存（force 模式下跳过）
  if (!force && cachedUser && (now - cacheTimestamp) < CACHE_TTL) {
    return cachedUser
  }
  
  try {
    const { useUserStore } = await import('../stores/user')
    const userStore = useUserStore()
    
    // ⭐ 第二级：检查 Pinia userStore（force 模式下跳过，确保过期 token 不信任缓存）
    if (!force && userStore.currentUser) {
      cachedUser = userStore.currentUser
      cacheTimestamp = now
      return cachedUser
    }
    
    // ⭐ 第三级：调用 store 的 fetchCurrentUser
    // force=true 时会绕过 store 内部缓存，直接请求 /users/me/
    const user = await userStore.fetchCurrentUser(force)
    
    cachedUser = user
    cacheTimestamp = now
    
    return user
  } catch (error) {
    console.error('Failed to get current user:', error)
    cachedUser = null
    cacheTimestamp = 0
    return null
  }
}

/**
 * 清除用户缓存（登出时调用）
 */
export function clearUserCache() {
  cachedUser = null
  cacheTimestamp = 0
}

/**
 * 创建权限检查函数
 * @param requiredPermissions - 需要的权限列表
 * @returns 权限检查函数
 */
export function createPermissionGuard(requiredPermissions: string[]) {
  return async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    try {
      const user = await getCurrentUser()
      if (!user || !checkPermissions(user, requiredPermissions)) {
        next('/403')
        return
      }
      next()
    } catch (error) {
      console.error('Permission guard error:', error)
      next('/403')
    }
  }
}

/**
 * 创建角色检查函数
 * @param requiredRoles - 需要的角色列表
 * @returns 角色检查函数
 */
export function createRoleGuard(requiredRoles: string[]) {
  return async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    try {
      const user = await getCurrentUser()
      if (!user || !checkRoles(user, requiredRoles)) {
        next('/403')
        return
      }
      next()
    } catch (error) {
      console.error('Role guard error:', error)
      next('/403')
    }
  }
}

/**
 * 无需认证守卫（用于登录页等公开页面）
 */
export function guestGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  if (TokenManager.hasValidAuth()) {
    next('/')
  } else {
    next()
  }
}

/**
 * 管理员权限守卫
 */
export const adminGuard = createRoleGuard(['operator'])

/**
 * 商户权限守卫
 */
export const merchantGuard = createRoleGuard(['merchant'])

/**
 * 企业权限守卫
 */
export const enterpriseGuard = createRoleGuard(['enterprise'])

/**
 * 超级用户权限守卫
 */
export function superUserGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  getCurrentUser().then(user => {
    if (user?.is_superuser) {
      next()
    } else {
      next('/403')
    }
  }).catch(error => {
    console.error('Super user guard error:', error)
    next('/403')
  })
}

// 声明路由元信息类型
declare module 'vue-router' {
  interface RouteMeta {
    // 是否需要认证，默认为true
    requiresAuth?: boolean
    // 需要的权限列表
    permissions?: string[]
    // 需要的角色列表
    roles?: string[]
    // 页面标题
    title?: string
    // 面包屑导航
    breadcrumbs?: Array<{ title: string; path?: string }>
    // 菜单ID（用于高亮当前菜单）
    menuId?: string
    // 是否在菜单中显示
    showInMenu?: boolean
    // 菜单图标
    icon?: string
    // 菜单排序
    sort?: number
  }
}